import {
  useCanAccess,
  useGetIdentity,
  useListContext,
  useStore,
  useTranslate,
} from "ra-core";
import { useEffect, useMemo } from "react";
import { matchPath, useLocation } from "react-router";
import { CreateButton } from "@/components/admin/create-button";
import { CanCreateHere } from "../misc/fromBitrix";
import { List } from "@/components/admin/list";
import { SelectInput } from "@/components/admin/select-input";
import { cn } from "@/lib/utils";

import { OnlyMineInput } from "../deals/OnlyMineInput";
import { TopToolbar } from "../layout/TopToolbar";
import { NoProjects } from "../projects/NoProjects";
import { ALL_PROJECTS, useCurrentProject } from "../projects/useCurrentProject";
import { AccountManagerInput } from "../sales/AccountManagerInput";
import type { Lead, PipelineStage } from "../types";
import { LeadBoard } from "./LeadBoard";
import { LeadCreate } from "./LeadCreate";
import { LeadEdit } from "./LeadEdit";
import { LeadRows } from "./LeadRows";
import { LeadShow } from "./LeadShow";
import { leadSourceChoices } from "./sources";

type LeadView = "board" | "list";

/**
 * «Лиды»: the board of the project chosen in the header (a column per stage
 * of its lead pipeline) or the same leads as a list. With «Все проекты»
 * there is no single board, so the leads of every project come as a list.
 * Filters: source, stage, responsible.
 */
const LeadList = () => {
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const { canAccess: canAccessSalesList, isPending } = useCanAccess({
    resource: "sales",
    action: "list",
  });
  const {
    currentId,
    isPending: projectsPending,
    pipelinesOf,
    stagesOf,
  } = useCurrentProject();
  const all = currentId === ALL_PROJECTS;
  const leadPipelineId =
    currentId != null && !all ? pipelinesOf(currentId, "lead")[0]?.id : null;
  const stagesKey = JSON.stringify(stagesOf(leadPipelineId ?? undefined));
  // a stable array: the board compares it to decide when to regroup
  const stages = useMemo(
    () => JSON.parse(stagesKey) as PipelineStage[],
    [stagesKey],
  );

  if (!identity || projectsPending) return null;
  if (currentId == null) return <NoProjects />;

  const filters = [
    <SelectInput
      key="source"
      source="source"
      choices={leadSourceChoices(translate)}
      label={false}
      emptyText="resources.leads.fields.source"
      helperText={false}
      alwaysOn
    />,
    ...(all
      ? []
      : [
          <SelectInput
            key="stage"
            source="stage_id"
            choices={stages}
            optionText="name"
            optionValue="id"
            label={false}
            emptyText="resources.leads.fields.stage_id"
            helperText={false}
            alwaysOn
          />,
        ]),
    ...(isPending
      ? []
      : [
          canAccessSalesList ? (
            <AccountManagerInput key="sales" source="sales_id" alwaysOn />
          ) : (
            <OnlyMineInput
              key="mine"
              source="sales_id"
              alwaysOn
              label="crm.leads.only_mine"
            />
          ),
        ]),
  ];

  return (
    <List
      resource="leads"
      perPage={500}
      filter={all ? {} : { project_id: currentId }}
      title={false}
      sort={{ field: "created_at", order: "DESC" }}
      filters={filters}
      actions={<LeadActions />}
      pagination={null}
    >
      <LeadLayout stages={stages} />
    </List>
  );
};

const LeadLayout = ({ stages }: { stages: PipelineStage[] }) => {
  const translate = useTranslate();
  const location = useLocation();
  const matchCreate = matchPath("/leads/create", location.pathname);
  const matchShow = matchPath("/leads/:id/show", location.pathname);
  const matchEdit = matchPath("/leads/:id", location.pathname);
  const { currentId } = useCurrentProject();
  const all = currentId === ALL_PROJECTS;
  const [view, setView] = useStore<LeadView>("leads.view", "board");
  const { data, isPending, filterValues, displayedFilters, setFilters } =
    useListContext<Lead>();

  // the stage filter of another project would empty the board
  const stageFilter = filterValues?.stage_id;
  useEffect(() => {
    if (stageFilter == null || !stages.length) return;
    if (!stages.some((s) => String(s.id) === String(stageFilter))) {
      const { stage_id: _, ...rest } = filterValues;
      setFilters(rest, displayedFilters);
    }
  }, [stageFilter, stages, filterValues, displayedFilters, setFilters]);

  const leads = data ?? [];
  const showBoard = !all && view === "board";

  return (
    <div className="w-full flex flex-col gap-4">
      {all ? (
        <p className="text-sm text-muted-foreground">
          {translate("crm.leads.choose_project")}
        </p>
      ) : (
        <nav
          aria-label={translate("crm.leads.view_label")}
          className="flex gap-1 rounded-lg bg-muted p-1 w-fit"
        >
          {(["board", "list"] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setView(v)}
              aria-current={view === v ? "page" : undefined}
              className={cn(
                "rounded-md px-4 py-1.5 text-sm font-medium transition-colors",
                view === v
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {translate(`crm.leads.view_${v}`)}
            </button>
          ))}
        </nav>
      )}
      {isPending ? null : showBoard ? (
        <LeadBoard stages={stages} leads={leads} />
      ) : (
        <LeadRows leads={leads} showProject={all} />
      )}
      <LeadCreate open={!!matchCreate} />
      <LeadEdit open={!!matchEdit && !matchCreate} id={matchEdit?.params.id} />
      <LeadShow open={!!matchShow} id={matchShow?.params.id} />
    </div>
  );
};

const LeadActions = () => (
  <TopToolbar className="flex-wrap">
    <CanCreateHere>
      <CreateButton label="resources.leads.action.new" />
    </CanCreateHere>
  </TopToolbar>
);

export default LeadList;
